import { useEffect, useState, useCallback } from "react";
import { Button, Box, TextField } from "@mui/material";
import Loading from "../../Loading";
import { getHeaders } from "../../utils/common";
import { DialogWrapper } from "../../components/general/DialogWrapper";
import ErrorDisplay from "../../components/error/ErrorDisplay";

/**
 * @typedef {interface} CreateSessionParams
 * @prop {boolean} isOpen
 * @prop {string} [forOrg] organisation id
 * @prop {string} [orgName] organisation name
 * @prop {string} [ownerId]
 * @prop {callback} onCreate
 * @prop {callback} closeModal
 */
interface CreateSessionParams {
  isOpen: boolean;
  forOrg?: string;
  orgName?: string;
  ownerId?: string;
  onCreate: (id: string) => void;
  closeModal: () => void;
}

/**
 * create session for organisation and admin.
 * when session creating from organisation we need to pass id of org.
 * @param {CreateSessionParams}
 * @returns {JSX.Element}
 */
function CreateSession({
  onCreate,
  forOrg,
  isOpen,
  closeModal,
  orgName,
  ownerId,
}: CreateSessionParams): JSX.Element {
  const [name, setName] = useState("");
  const [createError, setCreateError] = useState<Error | null>(null);
  const [isCreateInProgress, setIsCreateInProgress] = useState(false);

  const myHeaders = getHeaders();
  myHeaders.append("Content-Type", "application/json");

  useEffect(() => {
    setIsCreateInProgress(false);
    setName("");
    setCreateError(null);
  }, [isOpen]);

  const submitCreate = useCallback(
    (ctx: any) => {
      ctx.preventDefault();

      if (name === "") {
        setCreateError(new Error("A session name must be specified"));
        return;
      }

      setIsCreateInProgress(true);

      let subscribed = true;

      let url = `/mqttapi/v1/session`;

      if (forOrg !== undefined) {
        url = `/mqttapi/v1/organisation/${forOrg}/session`;
      }
      fetch(url, {
        method: "POST",
        headers: myHeaders,
        body: JSON.stringify({
          name: name,
          org_name: orgName ? orgName : "",
          owner_id: ownerId,
        }),
      })
        .then(async (response) => {
          if (response.ok) {
            return response.json();
          }
          const text = await response.text();
          throw new Error(`${response.statusText}: ${text}`);
        })
        .then((json) => (subscribed ? onCreate(json.id) : null))
        .catch((error) => (subscribed ? setCreateError(error) : null))
        .finally(() => setIsCreateInProgress(false));

      return () => {
        subscribed = false;
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [name, setIsCreateInProgress, onCreate, forOrg]
  );

  return (
    <DialogWrapper isOpen={isOpen} closeModal={closeModal} maxWidth="sm">
      <h4>New Session</h4>
      <Box
        component="form"
        sx={{
          "& > :not(style)": { m: 1 },
        }}
        noValidate
        autoComplete="off"
      >
        <TextField
          value={name}
          label="Add Name"
          color="primary"
          onChange={(e) => setName(e.target.value)}
          size="small"
          placeholder="test1"
        />
      </Box>
      {isCreateInProgress ? (
        <Loading action="Creating..." />
      ) : (
        <Box sx={{ display: "flex", justifyContent: "right", gap: "20px" }}>
          <Button variant="contained" color="secondary" onClick={submitCreate}>
            Create
          </Button>
          <Button variant="contained" color="primary" onClick={closeModal}>
            Cancel
          </Button>
        </Box>
      )}
      {createError !== null ? <ErrorDisplay err={createError} /> : null}
    </DialogWrapper>
  );
}

export default CreateSession;
